import { FinanceFactSummariesQuery } from 'generated/resources/graphql'

import { FinancialWorkspaceRecord } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'

/**
 * Transforms raw row data to period value
 */
export const transformRowToPeriodValue = (row: {
  columnLabel: string
  amountInCents: string
  amountWithPrecision?: string | null
}) => ({
  label: row.columnLabel,
  amountInCents: row.amountInCents,
  amountWithPrecision: row.amountWithPrecision ?? undefined,
})

/**
 * Transforms raw child data to record
 */
const transformChildToRecord = (child: {
  level: number,
  label: string
  rows: Array<{
    columnLabel: string
    amountInCents: string
    amountWithPrecision?: string | null
  }>
  categoryLabel?: string | null
  category?: string | null
}): FinancialWorkspaceRecord => ({
  isChild: true,
  id: child.label,
  level: child.level,
  label: child.label,
  hasChildren: false,
  category: child.category,
  categoryLabel: child.categoryLabel,
  periodValues: child.rows.map(transformRowToPeriodValue)
})

/**
 * Transforms raw item data to record
 */
export const transformItemToRecord = (item: {
  level: number
  label: string
  id: string | number
  resourceId?: string | number
  rows: Array<{
    columnLabel: string
    amountInCents: string
    amountWithPrecision?: string | null
  }>
  children?: Array<{
    label: string
    rows: Array<{
      columnLabel: string
      amountInCents: string
      amountWithPrecision?: string | null
    }>
    categoryLabel?: string | null
    category?: string | null
  }> | null
  categoryLabel?: string | null
  category?: string | null
  active?: boolean
  imageUrl?: string | null
  // Backend provides hasChildren for FinanceFactSummaries (may have children even if not loaded)
  hasChildren?: boolean
  // Flag to mark this as a child row (LineItem/CustomCategory result) - leaf node
  isChild?: boolean
}): FinancialWorkspaceRecord =>
  ({
    id: item.id,
    level: item.level,
    label: item.label,
    isChild: item.isChild,
    imageUrl: item.imageUrl,
    resourceId: item.resourceId,
    active: item.active ?? undefined,
    // Use backend hasChildren if provided, otherwise compute from children array
    category: item.category,
    categoryLabel: item.categoryLabel,
    periodValues: item.rows.map(transformRowToPeriodValue),
    hasChildren: item.hasChildren ?? !!(item.children && item.children.length > 0),
    children: item.children?.map((child) => transformChildToRecord({ ...child, level: item.level + 1 })),
  })



export const buildComparisonDataMap = (data: FinanceFactSummariesQuery | undefined, level: number, parentId?: string | number | undefined) => {
  if (!data) {
    return undefined
  }

  return data.financialWorkspace.financeFactSummariesConnection.edges.reduce<
    Record<string | number, FinancialWorkspaceRecord | null>
  >((acc, edge) => {
    const childId = parentId ? `${parentId}::${edge.node.id}` : edge.node.id
    const record = transformItemToRecord({
      level,
      id: childId,
      label: edge.node.label,
      rows: edge.node.rows,
      children: edge.node.children,
      categoryLabel: edge.node.categoryLabel,
      category: edge.node.category,
      active: edge.node.active ?? undefined,
      imageUrl: edge.node.imageUrl,
      hasChildren: edge.node.hasChildren,
    })

    acc[record.id] = record

    return acc
  }, {})
}